/**
 * SVG donut charts — no charting library.
 * Largest-first, clockwise from 12 o'clock; <3% → Others.
 * Colour via --color-chart-N + hatch patterns for greyscale.
 */

export type DonutInput = {
  key: string;
  label: string;
  value: number;
};

export type DonutSlice = {
  key: string;
  label: string;
  value: number;
  pct: number;
  /** Original keys merged into this slice (Others). */
  members: string[];
};

export const OTHERS_KEY = '__others__';
export const MIN_SLICE_PCT = 3;

export function prepareDonutSlices(
  items: DonutInput[],
  minPct = MIN_SLICE_PCT,
): DonutSlice[] {
  const positive = items.filter((i) => i.value > 0);
  const total = positive.reduce((s, i) => s + i.value, 0);
  if (total <= 0) return [];

  const sorted = [...positive].sort(
    (a, b) => b.value - a.value || a.label.localeCompare(b.label),
  );

  const keep: DonutInput[] = [];
  const small: DonutInput[] = [];
  for (const it of sorted) {
    if ((it.value / total) * 100 < minPct) small.push(it);
    else keep.push(it);
  }

  // If everything would be Others, skip grouping — show all slices.
  let slices: DonutInput[];
  if (keep.length === 0) {
    slices = sorted;
  } else if (small.length === 0) {
    slices = keep;
  } else {
    const othersValue = small.reduce((s, i) => s + i.value, 0);
    slices = [
      ...keep,
      {
        key: OTHERS_KEY,
        label: 'Others',
        value: othersValue,
      },
    ];
  }

  slices = [...slices].sort(
    (a, b) => b.value - a.value || a.label.localeCompare(b.label),
  );

  return slices.map((s) => {
    const members =
      s.key === OTHERS_KEY
        ? small.map((x) => x.key)
        : [s.key];
    return {
      key: s.key,
      label: s.label,
      value: s.value,
      pct: Math.round((s.value / total) * 1000) / 10,
      members,
    };
  });
}

function polar(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return {
    x: cx + r * Math.cos(rad),
    y: cy + r * Math.sin(rad),
  };
}

/** Arc path for a donut segment (outer → inner), angles in degrees from 12 o'clock clockwise. */
export function donutArcPath(
  cx: number,
  cy: number,
  rOuter: number,
  rInner: number,
  startAngle: number,
  endAngle: number,
): string {
  const sweep = endAngle - startAngle;
  if (sweep <= 0.001) return '';
  // Full circle: SVG arc can't draw 360° in one go — split.
  if (sweep >= 359.999) {
    const mid = startAngle + 180;
    return (
      donutArcPath(cx, cy, rOuter, rInner, startAngle, mid) +
      ' ' +
      donutArcPath(cx, cy, rOuter, rInner, mid, endAngle)
    );
  }
  const large = sweep > 180 ? 1 : 0;
  const o0 = polar(cx, cy, rOuter, startAngle);
  const o1 = polar(cx, cy, rOuter, endAngle);
  const i1 = polar(cx, cy, rInner, endAngle);
  const i0 = polar(cx, cy, rInner, startAngle);
  return [
    `M ${o0.x.toFixed(2)} ${o0.y.toFixed(2)}`,
    `A ${rOuter} ${rOuter} 0 ${large} 1 ${o1.x.toFixed(2)} ${o1.y.toFixed(2)}`,
    `L ${i1.x.toFixed(2)} ${i1.y.toFixed(2)}`,
    `A ${rInner} ${rInner} 0 ${large} 0 ${i0.x.toFixed(2)} ${i0.y.toFixed(2)}`,
    'Z',
  ].join(' ');
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export type DonutRenderOpts = {
  /** Unique id prefix for defs / data attrs */
  id: string;
  slices: DonutSlice[];
  totalLabel: string;
  totalSub?: string;
  formatValue: (v: number) => string;
  selectedKey?: string | null;
  ariaLabel: string;
};

/**
 * Greyscale-friendly patterns layered on chart colours.
 * Index wraps; pattern alone distinguishes adjacent slices when hue collapses.
 */
function patternDefs(uid: string): string {
  return `
    <defs>
      <pattern id="${uid}-p0" width="6" height="6" patternUnits="userSpaceOnUse">
        <path d="M0 6L6 0" stroke="var(--color-bg)" stroke-width="1.2" opacity="0.45"/>
      </pattern>
      <pattern id="${uid}-p1" width="5" height="5" patternUnits="userSpaceOnUse">
        <circle cx="1.5" cy="1.5" r="1.1" fill="var(--color-bg)" opacity="0.5"/>
      </pattern>
      <pattern id="${uid}-p2" width="6" height="6" patternUnits="userSpaceOnUse">
        <path d="M0 0H6M0 3H6" stroke="var(--color-bg)" stroke-width="1" opacity="0.4"/>
      </pattern>
      <pattern id="${uid}-p3" width="6" height="6" patternUnits="userSpaceOnUse">
        <path d="M0 0L6 6M6 0L0 6" stroke="var(--color-bg)" stroke-width="1" opacity="0.35"/>
      </pattern>
      <pattern id="${uid}-p4" width="4" height="4" patternUnits="userSpaceOnUse">
        <rect width="2" height="2" fill="var(--color-bg)" opacity="0.4"/>
      </pattern>
    </defs>`;
}

export function renderDonutHtml(opts: DonutRenderOpts): string {
  const { id, slices, totalLabel, totalSub, formatValue, selectedKey, ariaLabel } =
    opts;
  if (slices.length === 0) {
    return `<p class="muted">No data for this period.</p>`;
  }

  const total = slices.reduce((s, x) => s + x.value, 0);
  const size = 168;
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = 72;
  const rInner = 44;
  let angle = 0;

  const paths = slices
    .map((slice, i) => {
      const sweep = (slice.value / total) * 360;
      const start = angle;
      const end = angle + sweep;
      angle = end;
      const d = donutArcPath(cx, cy, rOuter, rInner, start, end);
      const series = (i % 5) + 1;
      const selected = selectedKey != null && selectedKey === slice.key;
      return `<path
        class="donut-slice chart-s${series}${selected ? ' is-selected' : ''}${selectedKey && !selected ? ' is-dimmed' : ''}"
        d="${d}"
        data-donut-id="${escapeHtml(id)}"
        data-slice-key="${escapeHtml(slice.key)}"
        role="button"
        tabindex="0"
        aria-pressed="${selected ? 'true' : 'false'}"
        aria-label="${escapeHtml(slice.label)} ${slice.pct}%, ${escapeHtml(formatValue(slice.value))}"
      >
        <title>${escapeHtml(slice.label)}: ${slice.pct}% · ${escapeHtml(formatValue(slice.value))}</title>
      </path>
      <path
        class="donut-slice-hatch"
        d="${d}"
        fill="url(#${id}-p${i % 5})"
        pointer-events="none"
        aria-hidden="true"
      />`;
    })
    .join('');

  const legend = slices
    .map((slice, i) => {
      const series = (i % 5) + 1;
      const selected = selectedKey != null && selectedKey === slice.key;
      return `<button type="button"
        class="donut-legend-row${selected ? ' is-selected' : ''}"
        data-donut-id="${escapeHtml(id)}"
        data-slice-key="${escapeHtml(slice.key)}"
        aria-pressed="${selected ? 'true' : 'false'}">
        <span class="donut-swatch chart-s${series}" aria-hidden="true"></span>
        <span class="donut-legend-label">${escapeHtml(slice.label)}</span>
        <span class="donut-legend-pct">${slice.pct}%</span>
        <span class="donut-legend-val muted">${escapeHtml(formatValue(slice.value))}</span>
      </button>`;
    })
    .join('');

  return `<div class="donut-chart" data-donut-root="${escapeHtml(id)}">
    <div class="donut-visual">
      <svg class="donut-svg" viewBox="0 0 ${size} ${size}" role="img" aria-label="${escapeHtml(ariaLabel)}">
        ${patternDefs(id)}
        ${paths}
      </svg>
      <div class="donut-centre" aria-hidden="true">
        <strong>${escapeHtml(totalLabel)}</strong>
        ${totalSub ? `<span class="muted">${escapeHtml(totalSub)}</span>` : ''}
      </div>
    </div>
    <div class="donut-legend" role="list">${legend}</div>
  </div>`;
}

/**
 * Toggle filter on slice/legend click. Second tap on same key clears.
 * Returns teardown.
 */
export function bindDonutFilter(
  root: HTMLElement,
  chartId: string,
  getSelected: () => string | null,
  setSelected: (key: string | null) => void,
): void {
  const nodes = root.querySelectorAll(
    `[data-donut-id="${CSS.escape(chartId)}"][data-slice-key]`,
  );
  nodes.forEach((el) => {
    const activate = () => {
      const key = (el as HTMLElement).dataset.sliceKey ?? null;
      if (!key) return;
      setSelected(getSelected() === key ? null : key);
    };
    el.addEventListener('click', activate);
    el.addEventListener('keydown', (e) => {
      const ke = e as KeyboardEvent;
      if (ke.key === 'Enter' || ke.key === ' ') {
        ke.preventDefault();
        activate();
      }
    });
  });
}

/** Whether a list row key matches the current donut filter. */
export function matchesDonutFilter(
  rowKey: string,
  selectedKey: string | null,
  slices: DonutSlice[],
): boolean {
  if (selectedKey == null) return true;
  const slice = slices.find((s) => s.key === selectedKey);
  if (!slice) return true;
  return slice.members.includes(rowKey) || slice.key === rowKey;
}
